import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CONSENT_TONE, G, errMsg } from "./guestApi";

export function ConsentDialog({ open, onClose, session, contact, onSaved }) {
  const [status, setStatus] = useState("UNKNOWN");
  const [evidence, setEvidence] = useState("");
  const [hist, setHist] = useState([]);

  useEffect(() => {
    if (!open || !contact) return;
    setStatus(contact.consent_status || "UNKNOWN"); setEvidence("");
    api.post(`${G}/consent-history`, { token: session.token, id: contact.id })
      .then((r) => setHist(r.data.rows || [])).catch(() => setHist([]));
  }, [open, contact, session.token]);

  if (!contact) return null;
  const cur = contact.consent_status || "UNKNOWN";
  const options = ["OPTED_IN", "OPTED_OUT", "UNKNOWN"].filter(
    (s) => !(cur === "OPTED_OUT" && s === "UNKNOWN"));

  const save = async () => {
    try {
      const r = await api.post(`${G}/consent`, { token: session.token, id: contact.id,
        status, evidence });
      toast.success("Consent updated"); onSaved(r.data.contact); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="guest-consent-dialog">
        <DialogHeader>
          <DialogTitle>Marketing consent · {contact.name}</DialogTitle>
          <DialogDescription>
            Opt-in needs evidence. An opt-out is kept and can only change with a new, evidenced opt-in.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          {options.map((s) => (
            <Button key={s} size="sm" variant={status === s ? "default" : "outline"}
              onClick={() => setStatus(s)} data-testid={`guest-consent-option-${s}`}>
              {s.replace("_", " ")}
            </Button>
          ))}
        </div>
        <Label>Evidence {status === "OPTED_IN" && "*"}</Label>
        <Textarea rows={2} value={evidence} onChange={(e) => setEvidence(e.target.value)}
          placeholder="e.g. Ticked WhatsApp offers box on feedback form, 12 May 2026"
          data-testid="guest-consent-evidence" />
        <div className="max-h-48 overflow-auto border rounded" data-testid="guest-consent-history">
          {hist.length === 0 && <div className="p-2 text-xs text-slate-500">No consent changes yet.</div>}
          {hist.map((h) => (
            <div key={h.id} className="px-2 py-1 text-xs border-b last:border-0">
              <Badge className={CONSENT_TONE[h.from_status]}>{h.from_status}</Badge> →{" "}
              <Badge className={CONSENT_TONE[h.to_status]}>{h.to_status}</Badge>
              <span className="ml-2 text-slate-500">{(h.at || "").slice(0, 16).replace("T", " ")} · {h.actor} · {h.source}</span>
              <div className="text-slate-600">{h.evidence}</div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={status === cur || (status === "OPTED_IN" && !evidence.trim())}
            data-testid="guest-consent-save">Save consent</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
